// Used in singular-assignment, composite-assignment, view-composite-assignment, student-assignment, grade-assignments, and workspace-details

// Angular
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";
import {
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

// RXJS/Store
import * as coursesStore from "#core/teacher-classes/store";
import * as profileStore from "#login/store";
import { Store, select } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Services/Types/Helpers
import { AssignmentStatusHelper } from "#helpers/assignment-status.helper";
import { DateFormatterHelper } from "#helpers/date-formatter.helper";
import { GetBookshelfUrlHelper } from "#helpers/get-bookshelf-url.helper";
import { LanguageHelper } from "#helpers/language.helper";
import { TimezoneHelper } from "#helpers/timezone.helper";
import { LocalStorage } from "#services/local-storage.service";
import { SnackBarService } from "#services/snackbar.service";
import { User as FidoUser } from "@carnegie-learning/data-access-fido";
import {
    PPAssignment as Assignment,
    CompositeTaskAssignment,
    Course,
    GradeAttempt,
    PPUser as User,
} from "cli-types";
// Components
import { GenericDialogComponent } from "#components/dialog-boxes/generic-dialog/generic-dialog.component";
import { AssignmentHelper } from "#helpers/assignment.helper";
import { Asset, CogTutorWorkspace, CustomActivity, ExtLink, assetActivityTypeIds, assetTypeIds, eBookActivity, eReader } from "@carnegie-learning/cure-types";
import { LTI_EDULASTIC_LAUNCH_TYPES } from "@carnegie-learning/data-access-angular-passport";

@Component({
    selector: "view-assignment",
    templateUrl: "./view-assignment.component.html",
    styleUrls: [
        "../../../styles/shared-styles.scss",
        "./view-assignment.component.scss",
    ],
    standalone: false
})
export class ViewAssignmentComponent implements OnInit, OnDestroy {
    asset: Asset | CogTutorWorkspace | undefined;
    readonly assetTypeIds = assetTypeIds;
    readonly assetActivityTypeIds = assetActivityTypeIds;
    course: Course;
    fidoUserData: FidoUser;

    shortAssignableType = "";

    loaded = false;
    hasFeedback = false;
    hasResponse = false;

    hasFileResponse = false;
    hasTextResponse = false;

    userProfile?: User;

    dataSource = new MatTableDataSource<GradeAttempt>();
    displayedColumns = ["attemptNumber", "submitted", "score"];
    loadTable = true;
    iframeURL: SafeResourceUrl | null = null;
    displayIFrame = false;
    readyCheckDestination = "";

    scorePossible: number | undefined;
    responseType = "";
    sectionId?: string;
    isWorldLanguageProgram = false;
    embedUrl: any;
    autoEvaluatedWithoutScore = false;
    genericDialogRef: MatDialogRef<GenericDialogComponent> | null = null;
    cogTutorSectionLabel = {
        name: "Activity Data",
        fontSize: "14px",
        icon: "info",
        infoTitle: "viewAssignmentInfoDialog",
    };
    attemptsSectionLabel = {
        name: "Attempts",
        fontSize: "14px",
    };
    answerIframeURL: SafeResourceUrl | null = null;
    showAnswerIframe = false;

    private ngUnsubscribe = new Subject();

    @Input() assignment?: Assignment | CompositeTaskAssignment;
    @Input() student?: User;
    @Input() loadReadyCheck?: boolean;
    @Input() studentView = false;

    constructor(
        private assignmentHelper: AssignmentHelper,
        private dateFormatterHelper: DateFormatterHelper,
        private timezoneHelper: TimezoneHelper,
        private snackBarService: SnackBarService,
        private localStorage: LocalStorage,
        private getBookshelfUrlHelper: GetBookshelfUrlHelper,
        public profile_store: Store<profileStore.UserState>,
        public store_courses: Store<coursesStore.CoursesState>,
        private cdr: ChangeDetectorRef,
        private assignmentStatusHelper: AssignmentStatusHelper,
        private domSanitizer: DomSanitizer,
        private languageHelper: LanguageHelper,
        public dialog: MatDialog,
        private getBookshelfUrl: GetBookshelfUrlHelper
    ) {}

    ngOnInit() {
        this.fidoUserData = this.localStorage.get("fidoUserProfile");

        this.profile_store
            .pipe(select(profileStore.getUserProfile))
            .subscribe((userProfile) => {
                if (userProfile) {
                    if (
                        this.student &&
                        this.student.userID === userProfile.userID
                    ) {
                        this.userProfile = this.student;
                        this.loadReadyCheck = true;
                        this.cdr.detectChanges();
                    } else {
                        this.userProfile = userProfile;
                    }
                }
            });

        this.store_courses
            .pipe(select(coursesStore.getCourseByRoute))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((course) => {
                if (!course) {
                    return;
                }
                this.course = course;
                this.isWorldLanguageProgram =
                    this.languageHelper.ifWorldLanguageProgram(
                        this.course.language.subjectId
                    );
            });
        if (this.assignment && !this.assignment.asset) {
            this.store_courses.dispatch(
                coursesStore.LoadAssignmentAsset({
                    assignmentId: this.assignment.assignmentID,
                    assetId: this.assignment.assetId,
                })
            );
        }
        if ((<CompositeTaskAssignment>this.assignment).asset) {
            const asset = (<CompositeTaskAssignment>this.assignment).asset;
            if (asset) {
                this.shortAssignableType = asset.assetTypeId;
                if (asset.scorePossible) {
                    this.scorePossible = asset.scorePossible;
                }
            }
            if (this.shortAssignableType === assetTypeIds.CUSTOM_ACT) {
                this.responseType = (<CustomActivity>asset).responseType;
            }
        } else {
            this.shortAssignableType = (<Assignment>(
                this.assignment
            )).shortAssignableType;
            const asset = (<Assignment>this.assignment).asset;
            if (this.shortAssignableType === assetTypeIds.CUSTOM_ACT) {
                this.responseType = (<CustomActivity>asset).responseType;
            }
            this.scorePossible = (<Assignment>this.assignment).scorePossible;
        }

        this.asset = this.getAsset();

        // retrieve TZ data from localStorage
        switch (this.shortAssignableType) {
            case assetTypeIds.EREADER:
                this.loaded = true;
                this.displayIFrame = true;
                if (this.assignment && this.userProfile) {
                    const url = this.getBookshelfUrl.activity(
                        <eReader>this.assignment.asset,
                        this.userProfile,
                        this.assignment.courseID,
                        this.assignment.assignmentID
                    );

                    this.embedUrl =
                        this.domSanitizer.bypassSecurityTrustResourceUrl(url);
                }
                break;
            case assetTypeIds.PERFORM:
            case assetTypeIds.CUSTOM_ACT:
            case assetTypeIds.C2C:
                this.loaded = true;
                this.displayIFrame = true;
                break;
            case assetTypeIds.COG_TUTOR:
                this.loaded = true;
                this.displayIFrame = true;
                this.sectionId = (<CogTutorWorkspace>this.asset)?.sectionId;
                break;
            case assetTypeIds.READYCHECK:
                this.readyCheckDestination =
                    this.userProfile?.roleLevel === 5
                        ? LTI_EDULASTIC_LAUNCH_TYPES.STUDENT_ASSIGNMENT
                        : LTI_EDULASTIC_LAUNCH_TYPES.TEACHER_REPORT;
                this.loaded = true;
                this.displayIFrame = true;
                break;
            case assetActivityTypeIds.MAKEQUEST:
                this.loaded = true;
                this.displayIFrame = true;
                this.iframeURL =
                    this.domSanitizer.bypassSecurityTrustResourceUrl(
                        "https://makequest.carnegielearning.com"
                    );

                break;
            case assetTypeIds.EBOOK_ACT:
                this.loaded = true;
                this.handleSpineData();
                break;
            case assetTypeIds.EXT_LINK:
            case assetActivityTypeIds.DEVSPACE:
                this.loaded = true;
                break;
        }
        if (this.assignment) {
            // used with responseType === 'both' to hide html elements when incomplete or response is null
            this.hasFileResponse = !(
                this.assignment.submissionFilePath &&
                this.assignment.submissionFilePath.trim() === "" &&
                this.assignment.submissionUrl === null
            );
            this.hasTextResponse = !!this.assignment.submissionText;
        }
    }

    getAsset() {
        if ((<CompositeTaskAssignment>this.assignment).asset) {
            return (<CompositeTaskAssignment>this.assignment).asset;
        }

        return (<Assignment>this.assignment).asset;
    }

    handleSpineData() {
        if (this.assignment?.gradeAttempts) {
            for (const attempt of this.assignment.gradeAttempts) {
                if (attempt.attemptID === null) {
                    // prevent invalid attempts from showing
                    continue;
                } else {
                    this.dataSource.data.push(attempt);
                }
            }
        }

        // check if any attempts were made if not display warning message
        if (
            this.assignment?.gradeAttempts === null ||
            this.dataSource.data.length === 0
        ) {
            this.loadTable = false;
        }

        this.autoEvaluatedWithoutScore = !!(
            this.assignment?.evaluatedAt &&
            this.assignment?.evaluatorScore === null
        );
        // Do not show score column if auto evaluated is false
        if (
            (<Assignment>this.assignment).teacherGraded && 
            !(<eBookActivity>(<CompositeTaskAssignment>this.assignment).asset).autoEvaluate 
        ) {
            this.displayedColumns = ["attemptNumber", "submitted"];
        } else if (
            (<CompositeTaskAssignment>this.assignment).asset &&
            !(<eBookActivity>(<CompositeTaskAssignment>this.assignment).asset)
                .autoEvaluate
        ) {
            this.displayedColumns = ["attemptNumber", "submitted"];
        }
    }



    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    getDate(date: string) {
        if (date && this.userProfile) {
            const newDate = this.timezoneHelper.convertServerDateToUserTZ(
                date,
                this.userProfile.timezone
            );
            return this.dateFormatterHelper.formatDateOneLinePretty(newDate);
        }
    }

    generateIFrameURL(obj: GradeAttempt) {
        this.displayIFrame = true;
        this.iframeURL = this.getBookshelfUrlHelper.getAttemptLink(obj);
    }

    closeIFrame() {
        this.displayIFrame = false;
    }

    hasSubmissionUrl() {
        return this.assignment?.submissionUrl !== null;
    }

    hasSubmissionFile() {
        return (
            this.assignment?.submissionFilePath !== "" &&
            this.assignment?.submissionFilePath !== null
        );
    }

    openSubmission(event: string) {
        if (this.assignment?.submissionUrl && event === "URL") {
            let url = this.assignment.submissionUrl;

            // If the URL does not start with http, append it.
            if (!url.startsWith("http")) {
                url = "http://" + url;
            }

            (<any>window).open(url, "_blank");
        } else if (
            this.assignment?.submissionFilePath !== "" &&
            event === "FILE"
        ) {
            (<any>window).open(this.assignment?.submissionFilePath, "_blank");
        }
    }

    hasSubmission(
        assignment: Assignment | CompositeTaskAssignment | undefined
    ) {
        return assignment && this.assignmentHelper.hasSubmission(assignment);
    }

    isMath(): boolean {
        if (this.asset) {
            return this.languageHelper.ifMathLanguage(this.asset.subjectId);
        } else {
            return false;
        }
    }

    hasLaunchLink(asset: Asset | undefined): boolean {
        return (
            this.isMath() &&
            asset?.assetTypeId === assetTypeIds.EXT_LINK &&
            !!(<ExtLink>asset).launchLink
        );
    }
    containerClassReturn(sat: string): string {
        let cssClass: string = "";
        if (!this.displayIFrame) {
            cssClass = "view-assignment-container ";
        }
        cssClass += sat + "_height100perc";
        return cssClass;
    }
}
